import React, { useMemo, useState, useRef } from "react";
import * as Yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { careersApiService } from "@/src/lib/api-services/careersApiServices";
import FormProvider from "@/src/components/hook-form/FormProvider";
import { RHFTextField, RHFAutocomplete } from "@/src/components/hook-form";
import CustomButton from "@/src/lib/ui/Button";
import TextField from "@/src/components/custom-fields/TextField";
import FileUpload from "@/src/components/upload";
import Iconify from "@/src/components/iconify";
import IconCode from "@/src/lib/iconCode";
import { useToast } from "@/src/lib/hooks/useToast";
import CustomChip from "@/src/lib/ui/customChip";
import { CustomRadioGroup } from "@/src/lib/ui/customRadioGroup";
import HodDesktopDatePicker from "@/src/lib/ui/hodDesktopDatePicker";
import useCommon from "@/src/lib/util/useCommon";
import { formatDate } from "@/src/components/common/common";
import { useHODMutation } from "@/src/lib/hooks/useHODMutation";

const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024; // 2MB

const educationLevels = ["School", "Undergraduate", "Postgraduate"];

export default function JobApplicationForm({ jobTitle, jobId }) {
    const { showToast, showCustomToast } = useToast();
    const { cityLists } = useCommon();
    const [uploadedFile, setUploadedFile] = useState(null);
    const [filePreviewUrl, setFilePreviewUrl] = useState(null);
    const [skills, setSkills] = useState([]);
    const [skillInput, setSkillInput] = useState("");
    const isGenericApplication = !jobId;
    // Use ref to avoid stale closure issues with uploadedFile in mutation callbacks
    const uploadedFileRef = useRef(null);

    const { mutate: uploadJobAttachmentMutate, isLoading: isUploading } =
        useHODMutation(
            (formData) => careersApiService.uploadJobAttachment(formData),
            {
                onSuccess: (response) => {
                    showCustomToast(
                        "Applied successfully! Please wait for the approval.",
                        "success"
                    );
                    handleReset();
                },
                onError: (error) => {
                    showCustomToast(
                        "Failed to apply! Please try again.",
                        "error"
                    );
                },
            }
        );

    const { mutate: applyJobMutate, isLoading: isApplying } = useHODMutation(
        (payload) => careersApiService.applyJob(payload),
        {
            onSuccess: (response) => {
                const isSuccess = response?.success;
                const applicationId = response?.data?.applicationId;

                if (!isSuccess) {
                    console.warn(
                        "applyJob succeeded but response.success is falsy",
                        response
                    );
                    // Still try to upload if we have applicationId and file
                    // Some APIs might return success=false but still provide applicationId
                }

                // Use ref to get current file value to avoid stale closure issues
                // This ensures we always have the latest file reference
                const currentFile = uploadedFileRef.current;

                // Validate applicationId - check for various falsy values
                const hasValidApplicationId =
                    applicationId !== null &&
                    applicationId !== undefined &&
                    applicationId !== "" &&
                    applicationId !== 0;

                // If file exists and we have a valid applicationId, upload it
                if (currentFile && hasValidApplicationId) {
                    console.log("Triggering file upload:", {
                        applicationId,
                        fileName: currentFile.name,
                        fileSize: currentFile.size,
                    });
                    try {
                        const formData = new FormData();
                        formData.append("applicationId", String(applicationId));
                        formData.append("file", currentFile);
                        uploadJobAttachmentMutate(formData);
                        return;
                    } catch (error) {
                        console.error("Error preparing file upload:", error);
                        showCustomToast(
                            "Error uploading resume. Please try again.",
                            "error"
                        );
                        return;
                    }
                }

                // Log when upload is skipped for debugging
                if (!currentFile) {
                    console.warn(
                        "Upload skipped: uploadedFile is null/undefined",
                        {
                            uploadedFileState: uploadedFile,
                            uploadedFileRef: uploadedFileRef.current,
                        }
                    );
                }
                if (!hasValidApplicationId) {
                    console.warn(
                        "Upload skipped: applicationId is invalid or missing",
                        {
                            applicationId,
                            response,
                            data: response?.data,
                            success: isSuccess,
                        }
                    );
                }

                handleReset();
            },
            onError: (error) => {
                showCustomToast("Failed to apply! Please try again.", "error");
            },
        }
    );

    const handleReset = () => {
        reset();
        setUploadedFile(null);
        uploadedFileRef.current = null; // Keep ref in sync
        setFilePreviewUrl(null);
        setSkills([]);
        setSkillInput("");
    };

    const ApplicationSchema = useMemo(
        () =>
            Yup.object().shape({
                firstName: Yup.string().required("First Name is required"),
                lastName: Yup.string().required("Last Name is required"),
                dob: Yup.string().required("Date of Birth is required"),
                gender: Yup.string().required("Gender is required"),
                phone: Yup.string()
                    .required("Phone Number is required")
                    .matches(
                        /^[0-9]{10}$/,
                        "Enter a valid 10 digit phone number"
                    ),
                email: Yup.string()
                    .required("Email is required")
                    .email("Enter a valid email address"),
                currentLocation: Yup.string().required(
                    "Current Location is required"
                ),
                preferredLocation: Yup.object()
                    .shape({
                        cityId: Yup.string().nullable(),
                        cityName: Yup.string().nullable(),
                    })
                    .nullable()
                    .required("Preferred Location is required"),
                educationLevel: Yup.string().required(
                    "Education Level is required"
                ),
                preferredPosition: isGenericApplication
                    ? Yup.string().required("Preferred position is required")
                    : Yup.string().nullable(),
                comments: Yup.string().nullable(),
            }),
        [isGenericApplication]
    );

    const defaultValues = useMemo(
        () => ({
            firstName: "",
            lastName: "",
            dob: "",
            gender: "",
            phone: "",
            email: "",
            currentLocation: "",
            preferredLocation: null,
            educationLevel: "",
            comments: "",
            preferredPosition: "",
        }),
        []
    );

    const locationOptions = useMemo(
        () => (Array.isArray(cityLists) ? cityLists : []),
        [cityLists]
    );

    const methods = useForm({
        resolver: yupResolver(ApplicationSchema),
        defaultValues,
    });

    const {
        handleSubmit,
        register,
        formState: { isSubmitting, errors },
        reset,
        setError,
        clearErrors,
    } = methods;

    const handleFileChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type - only PDF allowed
        if (file.type !== "application/pdf") {
            setUploadedFile(null);
            setFilePreviewUrl(null);
            setError("resume", {
                type: "manual",
                message: "Only PDF files are allowed",
            });
            return;
        }

        if (file.size > MAX_FILE_SIZE_BYTES) {
            setUploadedFile(null);
            setFilePreviewUrl(null);
            setError("resume", {
                type: "manual",
                message: "File size should not exceed 2MB",
            });
            return;
        }

        setUploadedFile(file);
        uploadedFileRef.current = file; // Keep ref in sync
        setFilePreviewUrl(URL.createObjectURL(file));
        clearErrors("resume");
    };

    const handleRemoveFile = () => {
        setUploadedFile(null);
        uploadedFileRef.current = null; // Keep ref in sync
        setFilePreviewUrl(null);
    };

    const handleAddSkill = () => {
        const value = skillInput.trim();
        if (!value) return;
        if (!skills.includes(value)) {
            setSkills((prev) => [...prev, value]);
        }
        setSkillInput("");
    };

    const handleRemoveSkill = (skill) => {
        setSkills((prev) => prev.filter((item) => item !== skill));
    };

    const onSubmit = async (data) => {
        // Validate resume upload - check both state and ref for safety
        const fileToUpload = uploadedFile || uploadedFileRef.current;
        if (!fileToUpload) {
            setError("resume", {
                type: "manual",
                message: "Resume is required",
            });
            return;
        }

        const formattedDob = data?.dob
            ? formatDate(data.dob, "YYYY-MM-DD")
            : "";
        const preferredPosition = data?.preferredPosition?.trim() || "";

        const payload = {
            comments: data?.comments || "",
            educationLevel: data.educationLevel,
            preferredLocation: data.preferredLocation,
            currentLocation: data.currentLocation,
            email: data.email,
            mobile: data.phone,
            gender: data.gender,
            dob: formattedDob,
            lastName: data.lastName,
            firstName: data.firstName,
            jobId: jobId ?? 1,
            jobTitle: jobTitle || preferredPosition,
            skills,
            hasResume: Boolean(uploadedFile),
        };

        applyJobMutate(payload);
    };

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {/* Compact Header */}
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 rounded-t-lg">
                <h3 className="text-base font-semibold text-gray-800">
                    {jobTitle ? (
                        <>
                            Apply for{" "}
                            <span className="text-red-600">{jobTitle}</span>
                        </>
                    ) : (
                        "Job Application"
                    )}
                </h3>
            </div>

            <div className="p-4">
                <FormProvider
                    methods={methods}
                    onSubmit={handleSubmit(onSubmit)}
                >
                    {/* All fields in a compact grid */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {isGenericApplication && (
                            <div className="col-span-2 md:col-span-3 lg:col-span-4">
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Preferred Position{" "}
                                    <span className="text-red-500">*</span>
                                </label>
                                <TextField
                                    size="small"
                                    placeholder="Role you are interested in"
                                    fullWidth
                                    {...register("preferredPosition")}
                                    error={Boolean(errors.preferredPosition)}
                                    helperText={
                                        errors.preferredPosition?.message
                                    }
                                />
                            </div>
                        )}

                        {/* First Name */}
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                First Name{" "}
                                <span className="text-red-500">*</span>
                            </label>
                            <RHFTextField
                                name="firstName"
                                size="small"
                                placeholder="First name"
                            />
                        </div>

                        {/* Last Name */}
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                Last Name{" "}
                                <span className="text-red-500">*</span>
                            </label>
                            <RHFTextField
                                name="lastName"
                                size="small"
                                placeholder="Last name"
                            />
                        </div>

                        {/* DOB */}
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                Date of Birth{" "}
                                <span className="text-red-500">*</span>
                            </label>
                            <Controller
                                name="dob"
                                control={methods.control}
                                render={({ field, fieldState }) => (
                                    <>
                                        <HodDesktopDatePicker
                                            value={field.value}
                                            onChange={field.onChange}
                                            placeholder="DD/MM/YYYY"
                                            disableFuture
                                            inputClassName="text-sm px-2.5 py-1.5"
                                            error={!!fieldState.error}
                                            helperText={
                                                fieldState.error?.message
                                            }
                                        />
                                    </>
                                )}
                            />
                        </div>

                        {/* Gender */}
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                Gender <span className="text-red-500">*</span>
                            </label>
                            <Controller
                                name="gender"
                                control={methods.control}
                                render={({ field, fieldState: { error } }) => (
                                    <>
                                        <CustomRadioGroup
                                            value={field.value}
                                            onChange={(event, value) =>
                                                field.onChange(value)
                                            }
                                            name="gender"
                                            className="flex items-center gap-3 mt-3"
                                        >
                                            <div className="flex items-center gap-1 text-sm text-gray-700">
                                                <input
                                                    type="radio"
                                                    value="Male"
                                                />
                                                <span>Male</span>
                                            </div>
                                            <div className="flex items-center gap-1 text-sm text-gray-700">
                                                <input
                                                    type="radio"
                                                    value="Female"
                                                />
                                                <span>Female</span>
                                            </div>
                                            <div className="flex items-center gap-1 text-sm text-gray-700">
                                                <input
                                                    type="radio"
                                                    value="Other"
                                                />
                                                <span>Other</span>
                                            </div>
                                        </CustomRadioGroup>
                                        {error && (
                                            <p className="mt-0.5 text-xs text-red-500">
                                                {error.message}
                                            </p>
                                        )}
                                    </>
                                )}
                            />
                        </div>

                        {/* Phone */}
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                Phone <span className="text-red-500">*</span>
                            </label>
                            <RHFTextField
                                name="phone"
                                size="small"
                                placeholder="10-digit number"
                            />
                        </div>

                        {/* Email */}
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                Email <span className="text-red-500">*</span>
                            </label>
                            <RHFTextField
                                name="email"
                                size="small"
                                placeholder="Email address"
                            />
                        </div>

                        {/* Current Location */}
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                Current Location{" "}
                                <span className="text-red-500">*</span>
                            </label>
                            <TextField
                                size="small"
                                placeholder="Your city"
                                fullWidth
                                {...register("currentLocation")}
                                error={Boolean(errors.currentLocation)}
                                helperText={errors.currentLocation?.message}
                            />
                        </div>

                        {/* Preferred Location */}
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                Preferred Location{" "}
                                <span className="text-red-500">*</span>
                            </label>
                            <RHFAutocomplete
                                name="preferredLocation"
                                placeholder="Select"
                                size="small"
                                options={locationOptions}
                                getOptionLabel={(option) =>
                                    option?.cityName || ""
                                }
                                isOptionEqualToValue={(option, value) =>
                                    option?.cityId === value?.cityId
                                }
                            />
                        </div>

                        {/* Education Level */}
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                Education{" "}
                                <span className="text-red-500">*</span>
                            </label>
                            <RHFAutocomplete
                                name="educationLevel"
                                placeholder="Select"
                                size="small"
                                options={educationLevels}
                                getOptionLabel={(option) => option || ""}
                                isOptionEqualToValue={(option, value) =>
                                    option === value
                                }
                            />
                        </div>

                        {/* Skills - takes remaining columns in the row */}
                        <div className="col-span-1 md:col-span-2 lg:col-span-3">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                Skills
                            </label>
                            <div className="flex flex-wrap items-center gap-1.5 border border-gray-300 rounded px-2 py-1.5 text-sm focus-within:ring-1 focus-within:ring-red-500 focus-within:border-red-500 min-h-[42px]">
                                {skills.map((skill) => (
                                    <CustomChip
                                        key={skill}
                                        label={skill}
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        onClick={() => handleRemoveSkill(skill)}
                                    />
                                ))}
                                <input
                                    type="text"
                                    value={skillInput}
                                    onChange={(e) =>
                                        setSkillInput(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            handleAddSkill();
                                        }
                                    }}
                                    className="flex-1 min-w-[80px] border-none outline-none focus:ring-0 text-sm bg-transparent"
                                    placeholder="Type & press Enter"
                                />
                            </div>
                        </div>

                        {/* Resume Upload - spans full row */}
                        <div className="col-span-2 md:col-span-3 lg:col-span-4">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                Resume <span className="text-red-500">*</span>{" "}
                                <span className="text-gray-400">(Max 2MB)</span>
                            </label>
                            {uploadedFile ? (
                                <div className="flex items-center gap-3 border border-gray-200 bg-white rounded px-3 py-2.5">
                                    <Iconify
                                        icon={IconCode.upload}
                                        width={18}
                                        className="text-gray-400 shrink-0"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p
                                            className="text-sm text-gray-700 truncate cursor-pointer hover:underline"
                                            onClick={() =>
                                                filePreviewUrl &&
                                                window.open(
                                                    filePreviewUrl,
                                                    "_blank"
                                                )
                                            }
                                        >
                                            {uploadedFile.name}
                                        </p>
                                        <p className="text-xs text-gray-400">
                                            {(uploadedFile.size / 1024).toFixed(
                                                1
                                            )}{" "}
                                            KB
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleRemoveFile}
                                        className="text-gray-400 hover:text-red-500 transition-colors"
                                    >
                                        <Iconify
                                            icon={IconCode.delete}
                                            width={16}
                                        />
                                    </button>
                                </div>
                            ) : (
                                <FileUpload
                                    buttonContent={
                                        <div className="flex flex-col items-center justify-center gap-1 py-4">
                                            <div className="flex items-center justify-center gap-2">
                                                <Iconify
                                                    width={16}
                                                    icon={IconCode.upload}
                                                    className="text-gray-500"
                                                />
                                                <span className="text-sm text-gray-600">
                                                    Upload file
                                                </span>
                                            </div>
                                            <p className="text-xs text-gray-400">
                                                Accepted format: .pdf
                                            </p>
                                        </div>
                                    }
                                    onChange={handleFileChange}
                                    accept="application/pdf"
                                    customButtonStyles="w-full border border-dashed border-gray-300 hover:border-red-400 rounded min-h-[34px] bg-white"
                                />
                            )}
                            {errors.resume && (
                                <p className="mt-0.5 text-xs text-red-500">
                                    {errors.resume.message}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Comments - full width, but compact */}
                    <div className="mt-3">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                            Comments{" "}
                            <span className="text-gray-400">(Optional)</span>
                        </label>
                        <TextField
                            multiline
                            rows={3}
                            size="small"
                            placeholder="Any additional information..."
                            fullWidth
                            {...register("comments")}
                            error={Boolean(errors.comments)}
                            helperText={errors.comments?.message}
                        />
                    </div>

                    {/* Submit Button */}
                    <div className="flex justify-end mt-4 pt-3 border-t border-gray-100">
                        <CustomButton
                            type="submit"
                            variant="contained"
                            color="error"
                            disabled={isSubmitting || isApplying || isUploading}
                            className="px-6"
                        >
                            {isSubmitting || isApplying || isUploading
                                ? "Submitting..."
                                : "Submit Application"}
                        </CustomButton>
                    </div>
                </FormProvider>
            </div>
        </div>
    );
}
